import { ChangeDetectorRef, Component, EventEmitter, Input, Output } from '@angular/core';
import { DefaultFormDialogComponent } from '../message/dialogs/default-form-dialog.component';
import { FormattingService } from '../services/formatting.service';
import { IExternalValueContext, IInvoiceLocation, IOrderEntryAddress, LocationDataType, OrderEntryLocationDataService } from '@app/main/order/entry/location/order-entry-location-data.service';
import { CountryDataService } from '../location/country-data.service';
import { GaValidateService } from '../formly/ga-validate.service';
import { ScrollService } from '../scroll/scroll.service';
import { TranslationService } from '../translation/translation.service';
import { IOrderResponse } from '@app/main/order/entry/order-entry-data.service';
import { IInvoiceInfo, InvoiceDataService } from './invoice-data.service';
import { GaFormlyService, IExtraFieldConfigurationObject } from '../formly/ga-formly.service';
import { util } from '../util';

import { ICompany, IContact, ICountry, ICountryExtended, IState } from '@type_definitions/datatypes';
import * as _ from 'underscore';
import { UntypedFormControl } from '@angular/forms';
import { Override } from '../app-decorators';

interface IInvoiceInfoModel {
  searchByCountry?:ICountry;
  searchByState?:IState;
  searchByInvoiceLocation?:IOrderEntryAddress;
  locationID?:number;
  createNewLocation?:boolean;
  locationInvoiceCompany?:string;
  locationInvoiceVATNumber?:string;
  locationInvoiceAddress?:string;
  locationInvoiceZipCode?:string;
  locationInvoiceCity?:string;
  locationInvoiceState?:IState;
  locationInvoiceCountry?:ICountry;
  contactInvoiceContact?:string;
  contactInvoiceEmail?:string;
  contactInvoicePhone?:string;
  externalId?:string;
  contact?:IContact;
}

@Component({
  selector: 'invoice-info',
  templateUrl: '../message/dialogs/default-form-dialog.html'
})
export class InvoiceInfoComponent extends DefaultFormDialogComponent {
  @Input() public orderData:IOrderResponse;

  @Output() public onSaving:EventEmitter<boolean> = new EventEmitter<boolean>();
  @Output() public onSaved:EventEmitter<IInvoiceInfo> = new EventEmitter<IInvoiceInfo>();

  // payload model when saving invoice info, because the model is different with our formly structure
  public payloadModel:IInvoiceInfo = {
    externalIdValue: undefined,
    location: undefined,
    createNewLocation: false,
    updateExternalId: false
  };
  public model:IInvoiceInfoModel = {};

  private locationCountryField:IExtraFieldConfigurationObject;
  private searchLocationField:IExtraFieldConfigurationObject;
  private searchCountryField:IExtraFieldConfigurationObject;
  private searchStateField:IExtraFieldConfigurationObject;
  private locationVATField:IExtraFieldConfigurationObject;
  private externalIdField:IExtraFieldConfigurationObject;
  private locationStateField:IExtraFieldConfigurationObject;
  private locationIdField:IExtraFieldConfigurationObject;
  private createNewLocationField:IExtraFieldConfigurationObject;
  private hideExternalValueField:boolean;

  constructor(
    private formlyService:GaFormlyService,
    private formattingService:FormattingService,
    private orderEntryLocationDataService:OrderEntryLocationDataService,
    private invoiceDataService:InvoiceDataService,
    private countryDataService:CountryDataService,
    private scrollService:ScrollService,
    private changeDetectorRef:ChangeDetectorRef,
    protected validateService:GaValidateService,
    translationService:TranslationService
  ) {
    super(validateService, translationService);
  }

  @Override(DefaultFormDialogComponent)
  public ngOnInit():void {
    this.entity = 'Invoice Address';
    super.ngOnInit();
    // initialize model
    this.initModel();

    // initialize form creation
    this.fields = this.createFields();
    this.fillFields();

    // initial invoice and search country states condition
    this.onInvoiceCountryChange(this.model.locationInvoiceCountry);
    this.onSearchByCountryChange(this.model.searchByCountry);
  }

  private initModel():void {
    // initialize formly model
    this.model = {
      searchByCountry: this.orderData.invoiceto?.location?.companyCountry?.country,
      searchByState: undefined,
      searchByInvoiceLocation: undefined,
      createNewLocation: false,
      locationID: this.orderData.invoiceto?.location?.id,
      locationInvoiceCompany: this.orderData.invoiceto?.location?.companyCountry?.company?.name,
      locationInvoiceVATNumber: this.orderData.invoiceto?.location?.vatNumber,
      locationInvoiceAddress: this.orderData.invoiceto?.location?.address,
      locationInvoiceZipCode: this.orderData.invoiceto?.location?.zipCode,
      locationInvoiceCity: this.orderData.invoiceto?.location?.city,
      locationInvoiceState: this.orderData.invoiceto?.location?.state,
      locationInvoiceCountry: this.orderData.invoiceto.location?.companyCountry?.country,
      contactInvoiceContact: this.orderData.invoiceto?.contact?.fullName,
      contactInvoiceEmail: this.orderData.invoiceto?.contact?.email,
      contactInvoicePhone: this.orderData.invoiceto?.contact?.directPhone,
      externalId: this.orderData.invoiceto?.externalValue?.externalValue,
      contact: this.orderData.invoiceto.contact
    };

    // if there is external id in context then we set initially in payloadModel
    if (this.orderData.context?.bpInvoiceLocationExternalId) {
      this.payloadModel.externalIdValue = {
        externalId: this.orderData.context?.bpInvoiceLocationExternalId,
        externalValue: this.orderData.invoiceto?.externalValue?.externalValue,
        id: this.orderData.invoiceto?.externalValue?.id
      };

      this.hideExternalValueField = this.orderExternalId?.gaIdAsExternalValue;
    }

    // if there is location in order entity data, then we store in payloadModel
    this.payloadModel.location = this.orderData.invoiceto?.location;
  }

  private get orderExternalId():IExternalValueContext {
    return util.notNullOrUndefinedOrEmpty(this.orderData.invoiceto?.externalValue?.externalId)? this.orderData.invoiceto?.externalValue?.externalId : this.orderData.context?.bpInvoiceLocationExternalId;
  }

  private getAllCountries():Promise<ICountry[]> {
    // this converts ICountryExtended[] to ICountry[] in order to match the country format from order info API
    // when they are not identically .onChange(this.onSearchCountryChanged) is triggered for second time which results in performance issues
    return this.countryDataService.getAllCountries()
      .then((countries:ICountryExtended[]) => {
        return _.map(countries, (country:ICountryExtended) => {
          return {
            id: country.id,
            name: country.name,
            isoCode: country.isoCode,
            hasState: country.hasState,
            vatMandatory: country.vatMandatory
          };
        });
      });
  }

  private fillFields():void {
    this.formlyService.fillField([this.searchCountryField, this.locationCountryField], this.getAllCountries());
  }

  private createFields():IExtraFieldConfigurationObject[] {
    const labelClass:string = 'col-sm-4';
    const valueClass:string = 'col-sm-8';

    const validationKey:string = 'existingLocationOrNewLocation';
    const validationMessage:string = 'Either create a new or select an existing location';

    return [
      this.formlyService.aTitle('Search')
        .build(),

      this.searchCountryField = this.formlyService.aSelect('searchByCountry', 'Search by country')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withFormat(this.formattingService.formatCountry)
        .onChange(this.onSearchByCountryChange)
        .build(),

      this.searchStateField = this.formlyService.aSelect('searchByState', 'Search by state')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withFormat(this.formattingService.formatState)
        .onChange(this.onSearchByStateChange)
        .build(),

      this.searchLocationField = this.formlyService.aSearch('searchByInvoiceLocation', 'Search Location')
        .isNotRequired()
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withFormat((orderEntryLocation:IOrderEntryAddress) => this.formattingService.formatLocationHtml(orderEntryLocation?.location))
        .withExtraOptions({
          templateOptions: {
            localSearch: true,
            minSearchLength: 2,
            parseHtml: true
          },
          validators: {
            [validationKey]: {
              expression: (control:UntypedFormControl):boolean => {
                // trigger error when there is no location id or empty location object
                if (!this.model.locationID && !this.model.createNewLocation) {
                  return (control.value || this.model.createNewLocation);
                }

                return true;
              },
              message: validationMessage
            }
          }
        })
        .onChange(this.onInvoiceLocationChange)
        .build(),

      this.createNewLocationField = this.formlyService.aCheckbox('createNewLocation', 'I want to create a new location')
        .withFormat(this.formattingService.formatBoolean)
        .onChange(this.onCreateNewLocation)
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withExtraOptions({
          validators: {
            [ validationKey ]: {
              expression: (control:UntypedFormControl):boolean => {
                // trigger error when there is no location id or empty location object
                if (!this.model.locationID && !this.model.searchByInvoiceLocation) {
                  return !!(control.value || this.model.searchByInvoiceLocation);
                }

                return true;
              },
              message: validationMessage
            }
          }
        })
        .build(),

      this.formlyService.aTitle('Invoice Location')
        .build(),

      this.locationIdField = this.formlyService.aValue('locationID', 'ID')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .build(),

      this.formlyService.anInput('locationInvoiceCompany', 'Invoice Company')
        .isRequired()
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .build(),

      this.locationVATField = this.formlyService.anInput('locationInvoiceVATNumber', 'Invoice VAT Number')
        .isRequired()
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .build(),

      this.formlyService.aGroup(undefined, 'Invoice Address')
        .withFields([
          this.formlyService.anInput('locationInvoiceAddress', 'Address')
            .isRequired()
            .withoutWrapper()
            .withWrapperClass('col-sm-12 col-space-bottom')
            .build(),

          this.formlyService.anInput('locationInvoiceZipCode', 'Zip Code')
            .isRequired()
            .withPlaceholder('Zip Code')
            .withoutWrapper()
            .withWrapperClass('col-4 col-xsm-5 col-sm-4 col-hug-right')
            .build(),

          this.formlyService.anInput('locationInvoiceCity', 'City')
            .isRequired()
            .withPlaceholder('City')
            .withoutWrapper()
            .withWrapperClass('col-8 col-xsm-7 col-sm-8 col-hug-left')
            .build()
        ])
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .build(),

      this.locationStateField = this.formlyService.aSelect('locationInvoiceState', 'Invoice State')
        .isRequired()
        .withFormat(this.formattingService.formatState)
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .build(),

      this.locationCountryField = this.formlyService.aSelect('locationInvoiceCountry', 'Invoice Country')
        .isRequired()
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withFormat(this.formattingService.formatCountry)
        .onChange(this.onInvoiceCountryChange)
        .build(),

      this.formlyService.aTitle('Contact')
        .build(),

      this.formlyService.aValue('contactInvoiceContact', 'Invoice Contact')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .build(),

      this.formlyService.aValue('contactInvoiceEmail', 'Invoice E-mail')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .build(),

      this.formlyService.aValue('contactInvoicePhone', 'Invoice Phone')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .build(),

      this.formlyService.aTitle('BP Internal ID')
        .withHideExpression(() => !this.payloadModel.externalIdValue || this.hideExternalValueField)
        .build(),

      this.externalIdField = this.formlyService.anInput('externalId', this.orderExternalId?.externalCode || 'External ID')
        .withLabelClass(labelClass)
        .withValueClass(valueClass)
        .withHideExpression(() => !this.payloadModel.externalIdValue || this.hideExternalValueField)
        .withPattern(this.orderExternalId?.externalValueRegex)
        .build()
    ];
  }

  private onSearchByCountryChange = (country:ICountry):void => {
    if (country) {
      this.searchStateField.formControl?.reset();

      // hide state field based on country if it has state or not
      this.searchStateField.hide = !country.hasState;

      if (country.hasState) {
        this.formlyService.fillField(this.searchStateField, this.countryDataService.getStates(this.model.searchByCountry.id));
      }

      this.onSearchByStateChange();
    }
  };

  private onSearchByStateChange = ():void => {
    this.formlyService.fillField(
      this.searchLocationField,
      this.orderEntryLocationDataService.locations(LocationDataType.INVOICE, this.model.searchByCountry.id, this.model.searchByState?.id || '', undefined, this.orderData.webshop?.id, this.orderData.country?.id, this.orderData.context?.billingType, this.orderData.bp?.location?.companyCountry?.id)
    );
  };

  private onInvoiceLocationChange = (selectedLocation:IInvoiceLocation):void => {
    if (selectedLocation) {
      this.externalIdField.templateOptions.label = selectedLocation?.externalValue?.externalId?.externalCode || this.orderExternalId?.externalCode;
      this.hideExternalValueField = util.notNullOrUndefinedOrEmpty(selectedLocation?.externalValue?.externalId?.gaIdAsExternalValue)? selectedLocation?.externalValue?.externalId?.gaIdAsExternalValue : this.orderExternalId?.gaIdAsExternalValue;

      // add timeout here to let external id field fully rendered in DOM
      setTimeout(() => {
        this.form.patchValue({
          contact: selectedLocation?.contact,
          locationID: selectedLocation?.location?.id,
          locationInvoiceCompany: selectedLocation?.location?.companyCountry?.company?.name,
          locationInvoiceVATNumber: selectedLocation?.location?.vatNumber,
          locationInvoiceAddress: selectedLocation?.location?.address,
          locationInvoiceZipCode: selectedLocation?.location?.zipCode,
          locationInvoiceCity: selectedLocation?.location?.city,
          locationInvoiceState: selectedLocation?.location?.state,
          locationInvoiceCountry: selectedLocation?.location?.companyCountry?.country,
          contactInvoiceContact: selectedLocation?.contact?.fullName || this.orderData.invoiceto?.contact?.fullName,
          contactInvoiceEmail: selectedLocation?.contact?.email || this.orderData.invoiceto?.contact?.email,
          contactInvoicePhone: selectedLocation?.contact?.directPhone || this.orderData.invoiceto?.contact?.directPhone,
          externalId: selectedLocation?.externalValue?.externalValue
        });
        this.createNewLocationField.formControl?.updateValueAndValidity();
      });

      this.payloadModel.location = selectedLocation?.location;
      this.payloadModel.externalIdValue = selectedLocation?.externalValue || {
        externalId: this.orderData.context?.bpInvoiceLocationExternalId,
        externalValue: undefined,
        id: undefined
      };
    } else {
      this.locationIdField.formControl.setValue(this.orderData.invoiceto?.location?.id);
      this.hideExternalValueField = this.orderExternalId?.gaIdAsExternalValue;
      this.createNewLocationField.formControl?.updateValueAndValidity();
    }
  };

  private onInvoiceCountryChange = (country:ICountry):void => {
    if (country) {
      this.locationVATField.templateOptions.required = country.vatMandatory;
      this.locationStateField.hide = !country.hasState;

      if (country.hasState) {
        this.formlyService.fillField(this.locationStateField, this.countryDataService.getStates(country.id));
      } else {
        this.locationStateField.formControl?.reset();
        if (this.payloadModel.location?.state) {
          delete this.payloadModel.location.state;
        }
      }
    }
  };

  private onCreateNewLocation = (isNew:boolean):void => {
    this.locationIdField.hide = isNew;
    this.searchLocationField.formControl?.updateValueAndValidity();
  };

  public save = ():void => {
    this.validateService.validateFields(this.fields, this.model, true)
      .then(() => {
        const model:IInvoiceInfoModel = util.copyAndExtend(this.model);
        const company:ICompany = _.extend(this.payloadModel.location?.companyCountry?.company || {}, {
          name: model.locationInvoiceCompany
        });

        // safely write object value
        this.payloadModel.location = _.extend(this.payloadModel.location || {}, {
          vatNumber: model.locationInvoiceVATNumber,
          address: model.locationInvoiceAddress,
          country: model.locationInvoiceCountry,
          state: model.locationInvoiceState,
          zipCode: model.locationInvoiceZipCode,
          city: model.locationInvoiceCity,
          companyCountry: _.extend(this.payloadModel.location?.companyCountry || {}, {
            country: model.locationInvoiceCountry,
            company
          })
        });

        if (!this.payloadModel.location?.state || _.isEmpty(this.payloadModel.location?.state)) {
          delete this.payloadModel.location.state;
        }

        if (model.createNewLocation) {
          // if user create new location then remove location.id and set companyRegisteredName
          this.payloadModel.location.id = undefined;
          this.payloadModel.location.companyRegisteredName = company && company.name;
        } else {
          this.payloadModel.location.id = model.locationID;
        }

        // payload that will be sent to server
        this.payloadModel.updateExternalId = model.externalId !== this.orderData.invoiceto.externalValue?.externalValue;
        this.payloadModel.createNewLocation = model.createNewLocation;

        // check if external id exists on the server
        if (this.payloadModel.externalIdValue) {
          this.payloadModel.externalIdValue.externalValue = model.externalId;
        }

        // this is coming from browselist service bindings
        this.setSaveBusy = true;
        this.invoiceDataService.updateInvoiceInfo(this.orderData.id, this.payloadModel)
          .then((invoiceInfo:IInvoiceInfo) => {
            if (model.createNewLocation) {
              // clear the cache after adding a new location to reflect it in the location list when reopening the dialog
              this.orderEntryLocationDataService.clear();
            }

            // send feedback to browselist that this info has been saved successfully
            this.onSaved.emit(invoiceInfo);
          })
          .catch(() => {
            // we only need to set false when error, since onSaved() will close dialog and close status message
            this.setSaveBusy = false;
          })
          .finally(() => {
            this.changeDetectorRef.detectChanges();
          });
      })
      .catch(() => {
        this.scrollService.scrollModalTop(0);
      })
      .finally(() => {
        this.changeDetectorRef.detectChanges();
      });
  };

  private set setSaveBusy(isBusy:boolean) {
    this.saveButtonBusy = isBusy;
    this.onSaving.emit(isBusy);
  }
}